import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowLeft,
    Users,
    Calendar,
    TrendingUp,
    DollarSign,
    BarChart3,
    Repeat,
    Lock,
    PieChart,
    Loader2,
    AlertTriangle,
    Download,
    X,
    Eye,
} from "lucide-react";
import {
    getAdminSubscriptionAnalytics,
    getAdminSubscriptionEconomics,
    getAdminLifecycleFunnelEvents,
} from "@/lib/api";

const PLAN_LABEL = {
    investor_lite: "Investor Lite",
    investor_pro:  "Investor Pro",
    investor_plus: "Investor Plus",
};

const fmtUSD = (n) =>
    typeof n === "number"
        ? `$${n.toLocaleString("en-US", { maximumFractionDigits: 0 })}`
        : "—";

function KpiCard({ label, value, sub, icon: Icon, testid, accent }) {
    return (
        <div
            className={
                "bl-card p-4 sm:p-5 "
                + (accent === "green" ? "bg-[var(--bl-green-soft)]/30 border-[var(--bl-green)]/30" : "")
            }
            data-testid={testid}
        >
            <div className="flex items-center justify-between gap-2">
                <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium">
                    {label}
                </div>
                {Icon && <Icon className={"h-4 w-4 " + (accent === "green" ? "text-[var(--bl-green)]" : "text-bl-muted")} />}
            </div>
            <div className="mt-2 font-display text-2xl sm:text-3xl font-semibold text-bl">
                {value}
            </div>
            {sub && (
                <div className="mt-1 text-xs text-bl-muted">{sub}</div>
            )}
        </div>
    );
}

function TierTable({ rows, columns }) {
    return (
        <div className="overflow-x-auto -mx-2 px-2">
            <table className="w-full text-sm">
                <thead>
                    <tr className="text-left text-[10px] tracking-[0.16em] uppercase text-bl-muted border-b border-bl">
                        <th className="py-2 pr-3 font-medium">Tier</th>
                        {columns.map((c) => (
                            <th key={c.key} className="py-2 px-3 font-medium text-right whitespace-nowrap">
                                {c.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.plan} className="border-b border-bl/40">
                            <td className="py-2 pr-3 text-bl font-medium whitespace-nowrap">
                                {PLAN_LABEL[r.plan] || r.plan}
                            </td>
                            {columns.map((c) => (
                                <td
                                    key={c.key}
                                    className="py-2 px-3 text-right font-mono-bl text-xs text-bl"
                                    data-testid={`tier-${r.plan}-${c.key}`}
                                >
                                    {c.render(r)}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default function AdminSubscriptionAnalytics() {
    const [analytics, setAnalytics] = useState(null);
    const [economics, setEconomics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const [a, e] = await Promise.all([
                    getAdminSubscriptionAnalytics(),
                    getAdminSubscriptionEconomics(),
                ]);
                setAnalytics(a);
                setEconomics(e);
            } catch (err) {
                setError(err?.response?.data?.detail || "Could not load analytics.");
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading) {
        return (
            <div className="min-h-screen bg-bl-bg flex items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-bl-muted" />
            </div>
        );
    }
    if (error) {
        return (
            <div className="min-h-screen bg-bl-bg flex items-center justify-center p-6">
                <div className="bl-card p-6 max-w-md flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                        <div className="font-semibold text-bl">Couldn't load analytics</div>
                        <p className="mt-1 text-sm text-bl-muted">{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    const tierRows = ["investor_lite", "investor_pro", "investor_plus"].map((plan) => {
        const r = analytics.revenue_by_tier[plan];
        const u = analytics.upload_utilization?.[plan] || {};
        const e = economics?.economics_by_tier?.[plan] || {};
        return {
            plan,
            monthly_subs: r.monthly_subs,
            annual_subs: r.annual_subs,
            mrr_usd: r.mrr_usd,
            arr_usd: r.arr_usd,
            avg_used: u.avg_used,
            avg_allowed: u.avg_allowed,
            utilization_pct: u.utilization_pct,
            arpu_usd: e.arpu_usd,
            reports_generated_30d: e.reports_generated_30d,
            estimated_ai_cost_30d_usd: e.estimated_ai_cost_30d_usd,
            cost_per_report_usd: e.cost_per_report_usd,
            gross_margin_pct: e.gross_margin_pct,
        };
    });

    return (
        <div className="min-h-screen bg-bl-bg">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
                <Link
                    to="/admin"
                    className="inline-flex items-center gap-1 text-sm text-bl-muted hover:text-bl mb-4"
                >
                    <ArrowLeft className="h-3.5 w-3.5" /> Back to admin
                </Link>
                <div className="bl-eyebrow">Subscription Analytics</div>
                <h1
                    className="font-display font-semibold text-bl text-3xl sm:text-4xl mt-1"
                    data-testid="admin-subscription-analytics-title"
                >
                    Subscriptions, Revenue & Economics
                </h1>
                <p className="mt-2 text-bl-muted text-sm max-w-2xl">
                    Live subscriber counts, annual-conversion funnel, upload
                    utilization, and per-tier economics. Numbers refresh on each
                    page load — no caching.
                </p>

                {/* === Headline KPIs === */}
                <div
                    className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4"
                    data-testid="admin-kpi-grid"
                >
                    <KpiCard
                        label="Total subscribers"
                        value={analytics.total_subscribers.toLocaleString()}
                        sub={`${analytics.most_used_tier ? PLAN_LABEL[analytics.most_used_tier] + " leads" : "—"}`}
                        icon={Users}
                        testid="kpi-total-subscribers"
                    />
                    <KpiCard
                        label="Monthly"
                        value={analytics.monthly_subscribers.toLocaleString()}
                        sub={`${(100 - analytics.annual_conversion_rate).toFixed(1)}% of base`}
                        icon={Calendar}
                        testid="kpi-monthly-subscribers"
                    />
                    <KpiCard
                        label="Annual"
                        value={analytics.annual_subscribers.toLocaleString()}
                        sub={`${analytics.annual_conversion_rate}% conversion rate`}
                        icon={TrendingUp}
                        testid="kpi-annual-subscribers"
                        accent="green"
                    />
                    <KpiCard
                        label="Switch-to-annual count"
                        value={analytics.switch_to_annual_count?.toLocaleString() || "0"}
                        sub="Monthly→annual flips"
                        icon={Repeat}
                        testid="kpi-switch-count"
                    />
                </div>

                {/* === Revenue + cash flow === */}
                <div className="mt-8">
                    <div className="bl-eyebrow mb-3">Revenue & cash flow</div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                        <KpiCard
                            label="MRR"
                            value={fmtUSD(analytics.total_mrr_usd)}
                            sub="Monthly recurring"
                            icon={DollarSign}
                            testid="kpi-mrr"
                        />
                        <KpiCard
                            label="ARR"
                            value={fmtUSD(analytics.total_arr_usd)}
                            sub="Annualized run-rate"
                            icon={BarChart3}
                            testid="kpi-arr"
                        />
                        <KpiCard
                            label="Cash locked in (annual)"
                            value={fmtUSD(analytics.upfront_cash_locked_in_usd)}
                            sub="Upfront from annual subs"
                            icon={Lock}
                            accent="green"
                            testid="kpi-upfront-cash"
                        />
                        <KpiCard
                            label="Untapped cash opportunity"
                            value={fmtUSD(analytics.upfront_cash_remaining_opportunity_usd)}
                            sub="If all monthly → annual today"
                            icon={PieChart}
                            testid="kpi-upfront-opportunity"
                        />
                    </div>
                </div>

                {/* === Per-tier subscribers + revenue === */}
                <section className="mt-8 bl-card p-5 sm:p-6" data-testid="subscribers-by-tier-section">
                    <div className="bl-eyebrow mb-1">Subscribers · Revenue · Utilization</div>
                    <h2 className="font-display font-semibold text-bl text-lg mt-1">
                        Per-tier breakdown
                    </h2>
                    <div className="mt-4">
                        <TierTable
                            rows={tierRows}
                            columns={[
                                { key: "monthly_subs", label: "Monthly", render: (r) => r.monthly_subs.toLocaleString() },
                                { key: "annual_subs", label: "Annual", render: (r) => r.annual_subs.toLocaleString() },
                                { key: "mrr_usd", label: "MRR", render: (r) => fmtUSD(r.mrr_usd) },
                                { key: "arr_usd", label: "ARR", render: (r) => fmtUSD(r.arr_usd) },
                                {
                                    key: "utilization_pct",
                                    label: "Util %",
                                    render: (r) => `${r.utilization_pct ?? 0}%`,
                                },
                                {
                                    key: "avg_used",
                                    label: "Avg used / allowed (30d)",
                                    render: (r) => `${r.avg_used ?? 0} / ${r.avg_allowed ?? 0}`,
                                },
                            ]}
                        />
                    </div>
                </section>

                {/* === Economics === */}
                {economics && (
                    <section className="mt-8 bl-card p-5 sm:p-6" data-testid="economics-section">
                        <div className="bl-eyebrow mb-1">Profitability</div>
                        <h2 className="font-display font-semibold text-bl text-lg mt-1">
                            Per-tier economics (last 30d)
                        </h2>
                        <p className="mt-1.5 text-xs text-bl-muted">
                            Estimated AI cost pulled from{" "}
                            <code className="font-mono-bl">openai_usage_log</code>, split
                            proportionally by reports generated. Total last 30d:{" "}
                            <strong className="text-bl">{fmtUSD(economics.ai_cost_last_30d_usd)}</strong>.
                        </p>
                        <div className="mt-4">
                            <TierTable
                                rows={tierRows}
                                columns={[
                                    { key: "reports_generated_30d", label: "Reports (30d)", render: (r) => r.reports_generated_30d ?? 0 },
                                    { key: "estimated_ai_cost_30d_usd", label: "Est. AI cost", render: (r) => fmtUSD(r.estimated_ai_cost_30d_usd) },
                                    { key: "cost_per_report_usd", label: "$/Report", render: (r) => `$${(r.cost_per_report_usd ?? 0).toFixed(3)}` },
                                    { key: "arpu_usd", label: "ARPU", render: (r) => fmtUSD(r.arpu_usd) },
                                    {
                                        key: "gross_margin_pct",
                                        label: "Gross margin",
                                        render: (r) => `${r.gross_margin_pct ?? 0}%`,
                                    },
                                ]}
                            />
                        </div>
                    </section>
                )}

                {/* === Lifecycle Email Funnel === */}
                {analytics?.lifecycle_email_funnel?.touchpoints && (
                    <LifecycleFunnelSection
                        funnel={analytics.lifecycle_email_funnel}
                    />
                )}

                {/* === Annual Cohort Retention === */}
                {analytics?.annual_cohorts?.cohorts && (
                    <CohortRetentionSection
                        data={analytics.annual_cohorts}
                    />
                )}
            </div>
        </div>
    );
}

// ─────────── Lifecycle email funnel section ───────────

const TOUCHPOINT_LABELS = {
    welcome_to_annual:        { name: "Welcome to annual",       lane: "Activation" },
    annual_renewal_reminder:  { name: "60-day renewal reminder", lane: "Retention" },
    payment_method_alert:     { name: "Payment-method alert",    lane: "Save" },
    dunning_day_4:            { name: "Dunning · Day 4",         lane: "Save" },
    dunning_day_7:            { name: "Dunning · Day 7",         lane: "Save" },
    winback_offer:            { name: "Winback offer (30d)",     lane: "Winback" },
};

const LANE_TINT = {
    Activation: "bg-[var(--bl-teal-tint)]/40 text-bl-primary",
    Retention:  "bg-[var(--bl-teal-tint)]/40 text-bl-primary",
    Save:       "bg-[var(--bl-amber-soft)]/40 text-[var(--bl-amber)]",
    Winback:    "bg-[var(--bl-green-soft)]/40 text-[var(--bl-green)]",
};

function LifecycleFunnelSection({ funnel }) {
    const order = [
        "welcome_to_annual",
        "annual_renewal_reminder",
        "payment_method_alert",
        "dunning_day_4",
        "dunning_day_7",
        "winback_offer",
    ];
    const rows = order
        .filter((k) => funnel.touchpoints[k])
        .map((k) => ({ key: k, ...funnel.touchpoints[k], ...TOUCHPOINT_LABELS[k] }));

    const [drillTouchpoint, setDrillTouchpoint] = useState(null);

    const handleExportCSV = () => {
        const header = [
            "touchpoint",
            "lane",
            "sent_30d",
            "sent_total",
            "suppressed_30d",
            "downstream_30d",
            "downstream_rate_pct",
        ];
        const lines = [header.join(",")];
        rows.forEach((r) => {
            lines.push([
                r.name,
                r.lane,
                r.sent_30d || 0,
                r.sent_total || 0,
                r.suppressed_30d || 0,
                r.downstream_30d || 0,
                r.downstream_rate_pct || 0,
            ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","));
        });
        const csv = lines.join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        const today = new Date().toISOString().slice(0, 10);
        a.href = url;
        a.download = `lifecycle-funnel-${today}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    return (
        <section
            className="mt-8 bl-card p-5 sm:p-6"
            data-testid="lifecycle-funnel-section"
        >
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="bl-eyebrow mb-1">Lifecycle email funnel</div>
                    <h2 className="font-display font-semibold text-bl text-lg mt-1">
                        Annual subscription touchpoints
                    </h2>
                </div>
                <button
                    type="button"
                    onClick={handleExportCSV}
                    data-testid="lifecycle-funnel-export-csv"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-bl text-xs font-medium text-bl hover:bg-[var(--bl-teal-tint)]/30 transition-colors"
                >
                    <Download className="h-3.5 w-3.5" />
                    Export CSV
                </button>
            </div>
            <p className="mt-1.5 text-xs text-bl-muted">
                Sends and downstream-action rates per email type, last {funnel.window_days} days.
                Downstream is a proxy (e.g. dunning emails → dunning rows resolved;
                winback → winback offers redeemed). Not perfect attribution but a useful directional signal for tuning copy + timing.
            </p>
            <div className="mt-4 overflow-x-auto -mx-2 px-2">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-[10px] tracking-[0.16em] uppercase text-bl-muted border-b border-bl">
                            <th className="py-2 pr-3 font-medium">Touchpoint</th>
                            <th className="py-2 px-3 font-medium">Lane</th>
                            <th className="py-2 px-3 font-medium text-right whitespace-nowrap">Sent (30d)</th>
                            <th className="py-2 px-3 font-medium text-right whitespace-nowrap">Lifetime</th>
                            <th className="py-2 px-3 font-medium text-right whitespace-nowrap">Suppressed (30d)</th>
                            <th className="py-2 px-3 font-medium text-right whitespace-nowrap">Downstream (30d)</th>
                            <th className="py-2 px-3 font-medium text-right whitespace-nowrap">Rate</th>
                            <th className="py-2 pl-3 font-medium text-right whitespace-nowrap">Details</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr
                                key={r.key}
                                className="border-b border-bl/40"
                                data-testid={`funnel-row-${r.key}`}
                            >
                                <td className="py-2 pr-3 text-bl font-medium whitespace-nowrap">
                                    {r.name}
                                </td>
                                <td className="py-2 px-3">
                                    <span className={"inline-flex items-center px-1.5 py-0.5 rounded text-[10px] tracking-[0.14em] uppercase font-medium " + (LANE_TINT[r.lane] || "")}>
                                        {r.lane}
                                    </span>
                                </td>
                                <td className="py-2 px-3 text-right font-mono-bl text-xs text-bl">
                                    {(r.sent_30d || 0).toLocaleString()}
                                </td>
                                <td className="py-2 px-3 text-right font-mono-bl text-xs text-bl-muted">
                                    {(r.sent_total || 0).toLocaleString()}
                                </td>
                                <td className="py-2 px-3 text-right font-mono-bl text-xs text-bl-muted">
                                    {(r.suppressed_30d || 0).toLocaleString()}
                                </td>
                                <td className="py-2 px-3 text-right font-mono-bl text-xs text-bl">
                                    {(r.downstream_30d || 0).toLocaleString()}
                                </td>
                                <td className="py-2 px-3 text-right font-mono-bl text-xs text-bl font-medium">
                                    {r.downstream_rate_pct || 0}%
                                </td>
                                <td className="py-2 pl-3 text-right">
                                    <button
                                        type="button"
                                        onClick={() => setDrillTouchpoint(r.key)}
                                        data-testid={`funnel-drilldown-${r.key}`}
                                        title={`View recent ${r.name} sends`}
                                        className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium text-bl-primary hover:bg-[var(--bl-teal-tint)]/40 transition-colors"
                                    >
                                        <Eye className="h-3 w-3" />
                                        View
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {drillTouchpoint && (
                <FunnelEventsModal
                    touchpoint={drillTouchpoint}
                    label={TOUCHPOINT_LABELS[drillTouchpoint]?.name || drillTouchpoint}
                    onClose={() => setDrillTouchpoint(null)}
                />
            )}
        </section>
    );
}

// ─────────── Lifecycle funnel drill-down modal ───────────

const STATUS_TINT = {
    sent:          "bg-[var(--bl-green-soft)]/40 text-[var(--bl-green)]",
    sandboxed:     "bg-[var(--bl-teal-tint)]/40 text-bl-primary",
    duplicate:     "bg-[var(--bl-amber-soft)]/40 text-[var(--bl-amber)]",
    unsubscribed:  "bg-[var(--bl-amber-soft)]/40 text-[var(--bl-amber)]",
    cadence_capped:"bg-[var(--bl-amber-soft)]/40 text-[var(--bl-amber)]",
    throttled:     "bg-[var(--bl-amber-soft)]/40 text-[var(--bl-amber)]",
    disabled:      "bg-bl-bg text-bl-muted",
    failed:        "bg-red-100 text-red-700",
};

function FunnelEventsModal({ touchpoint, label, onClose }) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        getAdminLifecycleFunnelEvents(touchpoint, { limit: 50, days: 30 })
            .then((d) => {
                if (!cancelled) setData(d);
            })
            .catch((e) => {
                if (!cancelled) setError(e?.response?.data?.detail || e?.message || "Failed to load events");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [touchpoint]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose]);

    const handleExportEventsCSV = () => {
        if (!data?.events?.length) return;
        const header = ["created_at", "sent_at", "status", "recipient", "user_name", "idempotency_key", "error_message"];
        const lines = [header.join(",")];
        data.events.forEach((e) => {
            lines.push([
                e.created_at || "",
                e.sent_at || "",
                e.status || "",
                e.recipient || "",
                e.user_name || "",
                e.idempotency_key || "",
                (e.error_message || "").replace(/\n/g, " "),
            ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","));
        });
        const csv = lines.join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        const today = new Date().toISOString().slice(0, 10);
        a.href = url;
        a.download = `${touchpoint}-events-${today}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
            onClick={onClose}
            data-testid="funnel-events-modal"
        >
            <div
                className="bl-card bg-white max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-bl">
                    <div>
                        <div className="bl-eyebrow mb-1">Touchpoint detail</div>
                        <h3 className="font-display font-semibold text-bl text-base">
                            {label} — recent sends
                        </h3>
                        <p className="text-xs text-bl-muted mt-0.5">
                            Last 30 days · Most recent 50 events
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={handleExportEventsCSV}
                            disabled={!data?.events?.length}
                            data-testid="funnel-events-export-csv"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-bl text-xs font-medium text-bl hover:bg-[var(--bl-teal-tint)]/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            <Download className="h-3.5 w-3.5" />
                            CSV
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            data-testid="funnel-events-modal-close"
                            className="p-1.5 rounded hover:bg-bl-bg transition-colors"
                            aria-label="Close"
                        >
                            <X className="h-4 w-4 text-bl-muted" />
                        </button>
                    </div>
                </div>
                <div className="flex-1 overflow-auto px-5 py-4">
                    {loading && (
                        <div className="flex items-center gap-2 text-sm text-bl-muted py-8 justify-center">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Loading events…
                        </div>
                    )}
                    {error && !loading && (
                        <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
                            <AlertTriangle className="h-4 w-4 mt-0.5" />
                            <span>{error}</span>
                        </div>
                    )}
                    {!loading && !error && data && (
                        data.events.length === 0 ? (
                            <div className="text-sm text-bl-muted py-8 text-center" data-testid="funnel-events-empty">
                                No events recorded for this touchpoint in the last {data.window_days} days.
                            </div>
                        ) : (
                            <table className="w-full text-sm" data-testid="funnel-events-table">
                                <thead>
                                    <tr className="text-left text-[10px] tracking-[0.16em] uppercase text-bl-muted border-b border-bl">
                                        <th className="py-2 pr-3 font-medium">When</th>
                                        <th className="py-2 px-3 font-medium">Status</th>
                                        <th className="py-2 px-3 font-medium">Recipient</th>
                                        <th className="py-2 px-3 font-medium">Note</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.events.map((e) => (
                                        <tr key={e.id} className="border-b border-bl/30" data-testid={`funnel-event-${e.id}`}>
                                            <td className="py-2 pr-3 whitespace-nowrap font-mono-bl text-xs text-bl-muted">
                                                {(e.created_at || "").slice(0, 19).replace("T", " ")}
                                            </td>
                                            <td className="py-2 px-3">
                                                <span className={"inline-flex items-center px-1.5 py-0.5 rounded text-[10px] tracking-[0.14em] uppercase font-medium " + (STATUS_TINT[e.status] || "bg-bl-bg text-bl-muted")}>
                                                    {e.status || "—"}
                                                </span>
                                            </td>
                                            <td className="py-2 px-3 text-bl">
                                                <div className="text-xs font-medium">{e.user_name || e.recipient || "—"}</div>
                                                {e.user_name && e.recipient && (
                                                    <div className="text-[10px] text-bl-muted">{e.recipient}</div>
                                                )}
                                            </td>
                                            <td className="py-2 px-3 text-xs text-bl-muted">
                                                {e.error_message ? (
                                                    <span className="text-red-700">{e.error_message}</span>
                                                ) : (
                                                    <span className="font-mono-bl text-[10px]">{(e.idempotency_key || "").slice(0, 24)}</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )
                    )}
                </div>
            </div>
        </div>
    );
}



// ─────────── Annual cohort retention heatmap ───────────

/** Map retention % to a calibrated background color so admins can scan
 * for the high-retention months at a glance. Lower retention slides
 * toward amber; higher retention toward green. */
function _retentionCellStyle(pct) {
    if (pct === null || pct === undefined) {
        // Cohort hasn't aged this far yet — neutral muted.
        return { background: "transparent", color: "var(--bl-muted)" };
    }
    if (pct >= 90) return { background: "rgba(31,133,90,0.18)", color: "var(--bl-green)" };
    if (pct >= 75) return { background: "rgba(31,133,90,0.10)", color: "var(--bl-text)" };
    if (pct >= 50) return { background: "rgba(207,148,46,0.10)", color: "var(--bl-text)" };
    if (pct >= 25) return { background: "rgba(207,148,46,0.18)", color: "var(--bl-amber)" };
    return { background: "rgba(154,42,42,0.10)", color: "#9A2A2A" };
}

function _fmtCohortMonth(ym) {
    if (!ym || ym.length < 7) return ym || "—";
    const [y, m] = ym.split("-");
    const monthLabels = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                         "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${monthLabels[parseInt(m, 10) - 1]} ${y}`;
}

function CohortRetentionSection({ data }) {
    const cohorts = data.cohorts || [];
    const maxMonth = data.max_month ?? 12;

    if (cohorts.length === 0) {
        return (
            <section
                className="mt-8 bl-card p-5 sm:p-6"
                data-testid="cohort-retention-section"
            >
                <div className="bl-eyebrow mb-1">Annual cohort retention</div>
                <p className="mt-2 text-sm text-bl-muted">
                    No annual subscribers yet — once you have some, their retention
                    curves by signup month will plot here automatically.
                </p>
            </section>
        );
    }

    // Recent cohorts at the top — easier to compare what's happening now.
    const ordered = [...cohorts].sort(
        (a, b) => b.cohort_month.localeCompare(a.cohort_month),
    );

    return (
        <section
            className="mt-8 bl-card p-5 sm:p-6"
            data-testid="cohort-retention-section"
        >
            <div className="bl-eyebrow mb-1">Annual cohort retention</div>
            <h2 className="font-display font-semibold text-bl text-lg mt-1">
                Are annual subscribers actually sticking?
            </h2>
            <p className="mt-1.5 text-xs text-bl-muted max-w-2xl">
                Each row is a signup-month cohort; each column is months elapsed since
                signup. Cells show % of the cohort still active at that month.
                Blank cells mean the cohort hasn&apos;t aged that far yet.
            </p>
            <div className="mt-4 overflow-x-auto -mx-2 px-2">
                <table className="w-full text-sm border-separate border-spacing-0">
                    <thead>
                        <tr className="text-[10px] tracking-[0.16em] uppercase text-bl-muted">
                            <th className="py-2 pr-3 font-medium text-left whitespace-nowrap border-b border-bl">Cohort</th>
                            <th className="py-2 px-2 font-medium text-right whitespace-nowrap border-b border-bl">Size</th>
                            {Array.from({ length: maxMonth + 1 }, (_, m) => (
                                <th
                                    key={m}
                                    className="py-2 px-2 font-medium text-center whitespace-nowrap border-b border-bl"
                                >
                                    M+{m}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {ordered.map((c) => (
                            <tr
                                key={c.cohort_month}
                                data-testid={`cohort-row-${c.cohort_month}`}
                            >
                                <td className="py-1.5 pr-3 font-medium text-bl whitespace-nowrap border-b border-bl/40">
                                    {_fmtCohortMonth(c.cohort_month)}
                                </td>
                                <td className="py-1.5 px-2 text-right font-mono-bl text-xs text-bl-muted border-b border-bl/40">
                                    {c.size.toLocaleString()}
                                </td>
                                {c.retention.map((r) => {
                                    const style = _retentionCellStyle(r.retention_pct);
                                    return (
                                        <td
                                            key={r.month_offset}
                                            className="py-1.5 px-2 text-center font-mono-bl text-xs border-b border-bl/40"
                                            style={style}
                                            data-testid={`cohort-cell-${c.cohort_month}-${r.month_offset}`}
                                            title={
                                                r.retention_pct === null
                                                    ? `Cohort hasn't aged this far yet`
                                                    : `${r.retained}/${r.reached} retained at month ${r.month_offset}`
                                            }
                                        >
                                            {r.retention_pct === null
                                                ? "—"
                                                : `${r.retention_pct}%`}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="mt-3 flex flex-wrap gap-3 text-[10px] text-bl-muted">
                <span><span className="inline-block w-3 h-3 rounded mr-1 align-middle" style={{ background: "rgba(31,133,90,0.18)" }} />≥90% retained</span>
                <span><span className="inline-block w-3 h-3 rounded mr-1 align-middle" style={{ background: "rgba(31,133,90,0.10)" }} />75–89%</span>
                <span><span className="inline-block w-3 h-3 rounded mr-1 align-middle" style={{ background: "rgba(207,148,46,0.10)" }} />50–74%</span>
                <span><span className="inline-block w-3 h-3 rounded mr-1 align-middle" style={{ background: "rgba(207,148,46,0.18)" }} />25–49%</span>
                <span><span className="inline-block w-3 h-3 rounded mr-1 align-middle" style={{ background: "rgba(154,42,42,0.10)" }} />&lt;25%</span>
            </div>
        </section>
    );
}
